import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { RovoSettingsModal } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal';

import { isFirstPartyLink } from '@confluence/content-smartlinks/entry-points/smartLinkBatchHelpers.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	GOOGLE_DRIVE_PROVIDER_KEY,
	useSmartlinkConnectionLinkCount,
} from '../../../banners/hooks/useSmartlinkConnectionLinkCount.ts';
import { useConnectorOrgAuthRates } from '../../../banners/hooks/useConnectorOrgAuthRates.ts';
import { useOrgConnectedAppsRates } from '../../../banners/hooks/useOrgConnectedAppsRates.ts';
import { useGdriveSmartLinkAuth } from '../../../footer/hooks/useGdriveSmartLinkAuth.ts';
import type { EnhancedSmartLink } from '../../../utils/smartLinkTypes.ts';
import {
	collectUnconnectedTraitKeys,
	isUnconnectedThirdParty,
	pickTopUnconnectedApp,
} from '../../../utils/unconnectedThirdPartyApps.ts';
import { ConnectorPromoBanner } from '../ConnectorPromoBanner.tsx';
import type { ConnectorPromoBannerVariant } from '../ConnectorPromoBanner.tsx';
import {
	getBannerClickedEvent,
	getBannerNotShownEvent,
	getBannerShownEvent,
	type GdriveIneligibleReason,
	type OrgIneligibleReason,
	type PromoBannerAttributes,
} from '../promoBannerAnalytics.ts';

import { useConnectableApps } from './useConnectableApps.ts';

/** The gdrive banner reports `google` to line up with the org banner's trait keys. */
const GDRIVE_ANALYTICS_CONNECTOR = 'google';

/** Attributed separately from the preview panel's "Manage connections" entry point. */
const TEAMWORK_GRAPH_MODAL_SURFACE = 'confluence-teamwork-graph-modal';

/** The connector dialog's "Connect apps" tab — the banner is only ever about that one. */
const APPS_TAB_ID = 'discovery';

/**
 * `ORG_CONNECTED_APPS_RATE_TRAIT` is keyed by smartlink provider family, which is coarser
 * than the `ProductKeys3P` the icons need — `google` covers Drive, Gmail and Calendar, so
 * Drive stands in for the family. Keys that already agree pass through.
 */
const PROVIDER_FAMILY_TO_PRODUCT_KEY: Record<string, ProductKeys3P> = {
	google: ProductKeys3P.GoogleDrive,
};

const toProductKeyRates = (
	rates: Record<string, number> | undefined,
): Record<string, number> | undefined => {
	if (!rates) {
		return undefined;
	}
	const mapped: Record<string, number> = {};
	for (const [family, percent] of Object.entries(rates)) {
		mapped[PROVIDER_FAMILY_TO_PRODUCT_KEY[family] ?? family] = percent;
	}
	return mapped;
};

export type UseConnectorPromoBannerInput = {
	/** Passed in, not fetched: the modal already collects these for "Links to". */
	links: EnhancedSmartLink[];
};

export type UseConnectorPromoBannerResult = {
	element: React.ReactElement | null;
};

/**
 * Picks the banner variant, first match winning:
 *
 * 1. **Google Drive** — 1P-only page, the org already links to Drive, this user has not
 *    connected it.
 * 2. **Org connected apps** — an unauthorized 3P link whose app the org already uses.
 * 3. **Generic** — neither.
 *
 * A losing variant's first failed check rides along on the `viewed` event, so a generic
 * banner still says why the other two were ruled out.
 *
 * Returns `null` without Rovo — where the dialog the banner opens is no use — and while
 * the deciding traits load, so the copy never changes under the user.
 */
export const useConnectorPromoBanner = ({
	links,
}: UseConnectorPromoBannerInput): UseConnectorPromoBannerResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, isRovoLLMEnabled } = useSessionData();

	const isPageFirstPartyOnly = links.length > 0 && links.every((l) => isFirstPartyLink(l.url));

	const { value: linkCounts, loading: isLinkCountLoading } = useSmartlinkConnectionLinkCount({
		skip: !isPageFirstPartyOnly,
	});
	const hasSiteWideDriveLinks = (linkCounts?.[GOOGLE_DRIVE_PROVIDER_KEY] ?? 0) >= 1;

	const { isAuthenticated: isGdriveConnected, isLoading: isGdriveAuthLoading } =
		useGdriveSmartLinkAuth();

	const unconnectedThirdPartyLinks = useMemo(() => links.filter(isUnconnectedThirdParty), [links]);
	const hasUnconnectedThirdParty = unconnectedThirdPartyLinks.length > 0;

	const { value: orgRates, loading: isRatesLoading } = useOrgConnectedAppsRates({
		skip: !hasUnconnectedThirdParty,
	});
	const topApp = useMemo(
		() => pickTopUnconnectedApp(orgRates, collectUnconnectedTraitKeys(unconnectedThirdPartyLinks)),
		[orgRates, unconnectedThirdPartyLinks],
	);

	// The trait hooks share one store with a global `loading` flag, so each is only
	// honoured when its branch is in play.
	const isLoading =
		(isPageFirstPartyOnly && (isLinkCountLoading || isGdriveAuthLoading)) ||
		(hasUnconnectedThirdParty && isRatesLoading);

	// First failing check, in order. `undefined` means the variant qualified — so these
	// double as the variant decision below, and cannot drift from it.
	const gdriveIneligibleReason: GdriveIneligibleReason | undefined = useMemo(() => {
		if (!isPageFirstPartyOnly) {
			return 'notFirstPartyOnly';
		}
		if (!hasSiteWideDriveLinks) {
			return 'noSiteWideDriveLinks';
		}
		if (isGdriveConnected) {
			return 'alreadyAuthenticated';
		}
		return undefined;
	}, [isPageFirstPartyOnly, hasSiteWideDriveLinks, isGdriveConnected]);

	const orgIneligibleReason: OrgIneligibleReason | undefined = useMemo(() => {
		if (!hasUnconnectedThirdParty) {
			return 'noUnconnectedThirdParty';
		}
		if (!topApp) {
			return 'noTopApp';
		}
		return undefined;
	}, [hasUnconnectedThirdParty, topApp]);

	const variant: ConnectorPromoBannerVariant =
		gdriveIneligibleReason === undefined
			? 'gdrive'
			: orgIneligibleReason === undefined
				? 'orgConnectedApps'
				: 'generic';

	const { value: connectorAuthRates } = useConnectorOrgAuthRates();

	// The org variant ranks its icons by the same trait its copy quotes, so the two agree.
	// The other variants quote no adoption figure, so they fall back to the Rovo connector
	// trait — the same one the CWR ghost icons rank on.
	const iconRates = useMemo(
		() => (variant === 'orgConnectedApps' ? toProductKeyRates(orgRates) : connectorAuthRates),
		[variant, orgRates, connectorAuthRates],
	);

	// The Drive variant names Google Drive, so its icons lead with Drive.
	const { apps } = useConnectableApps({
		pinnedProductKey: variant === 'gdrive' ? ProductKeys3P.GoogleDrive : undefined,
		rates: iconRates,
	});

	const shownAttributes: PromoBannerAttributes = useMemo(() => {
		if (variant === 'gdrive') {
			return {
				bannerType: 'gdrive',
				connector: GDRIVE_ANALYTICS_CONNECTOR,
				ghostIconCount: apps.length,
			};
		}
		if (variant === 'orgConnectedApps' && topApp) {
			return {
				bannerType: 'orgConnectedApps',
				connector: topApp.traitKey,
				topAppPercent: topApp.percent,
				gdriveIneligibleReason,
				ghostIconCount: apps.length,
			};
		}
		return {
			bannerType: 'generic',
			gdriveIneligibleReason,
			orgIneligibleReason,
			ghostIconCount: apps.length,
		};
	}, [variant, topApp, gdriveIneligibleReason, orgIneligibleReason, apps.length]);

	// Once per mount, on the variant first seen. Exactly one of `viewed` and `notShown`
	// fires, so `clicked / viewed` stays a click-through rate.
	const hasFiredRef = useRef(false);
	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		if (!isRovoLLMEnabled) {
			hasFiredRef.current = true;
			createAnalyticsEvent(getBannerNotShownEvent({ reason: 'rovoDisabled' })).fire();
			return;
		}
		if (isLoading) {
			return;
		}
		hasFiredRef.current = true;
		createAnalyticsEvent(getBannerShownEvent(shownAttributes)).fire();
	}, [isRovoLLMEnabled, isLoading, shownAttributes, createAnalyticsEvent]);

	const fireClickedEvent = useCallback(() => {
		createAnalyticsEvent(getBannerClickedEvent(shownAttributes)).fire();
	}, [createAnalyticsEvent, shownAttributes]);

	// Pressing the banner opens `RovoSettingsModal`, which a site without Rovo has no use
	// for, so the surface goes rather than leading somewhere dead.
	if (!isRovoLLMEnabled || isLoading) {
		return { element: null };
	}

	return {
		element: (
			<RovoSettingsModal
				product="confluence"
				cloudId={cloudId}
				surface={TEAMWORK_GRAPH_MODAL_SURFACE}
			>
				{({ open }) => (
					<ConnectorPromoBanner
						variant={variant}
						topAppPercent={topApp?.percent}
						apps={apps}
						onClick={() => {
							fireClickedEvent();
							open({ defaultTab: APPS_TAB_ID });
						}}
					/>
				)}
			</RovoSettingsModal>
		),
	};
};
